/**
 * @fileoverview Step 1: Document Your Home's Issues Page
 * @module Step2DocumentPage
 * 
 * Business Context:
 * - Guides users through understanding and documenting deferred maintenance
 * - 7 contextual video slides explaining deferred maintenance concepts
 * - After watching videos, users access category checklists
 * - All content accessible from start (not gated)
 * - This is Step 1 in the 4-step journey (Document → File → Flaws → Arguments)
 * 
 * Layout:
 * - LEFT: Course navigation (video list + checklists)
 * - RIGHT: Video player OR inline checklist activity
 * 
 * Content Flow:
 * 1. "What would need to be fixed to sell your home tomorrow?"
 * 2. Definition of Deferred Maintenance
 * 3. Why Deferred Maintenance Matters for Your Appeal
 * 4. Examples of Deferred Maintenance
 * 5. What is NOT Deferred Maintenance
 * 6. The Power of Documentation
 * 7. "Find Your Issues with Your Home" walkthrough
 * → Checklists appear below video 7 in navigation (collapsible)
 * → Clicking a checklist renders activity inline on right side
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import VideoPlayer from '../components/course/VideoPlayer';
import {
  RoofChecklist,
  FoundationChecklist,
  HVACChecklist,
  PlumbingChecklist,
  ElectricalChecklist,
  ChecklistSummary
} from '../components/maintenance';
import {
  ArrowLeft,
  ClipboardCheck,
  Home,
  Layers,
  Wind,
  Droplets,
  Zap,
  Play,
  CheckCircle2,
  Clock,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  BookOpen,
  ListChecks
} from 'lucide-react';

interface VideoContent {
  id: string;
  title: string;
  description: string;
  duration: string;
  videoUrl: string;
  isPlaceholder?: boolean;
  isActionVideo?: boolean;
}

type Category = 'ROOF' | 'FOUNDATION' | 'HVAC' | 'PLUMBING' | 'ELECTRICAL';

interface CategoryInfo {
  key: Category;
  title: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  bgColor: string;
}

// Module ID for Step 1 (Document Home) - module-02 contains Document Home content
const STEP2_MODULE_ID = 'module-02';

const CATEGORIES: CategoryInfo[] = [
  { 
    key: 'ROOF', 
    title: 'Roof', 
    icon: <Home className="w-5 h-5" />,
    description: 'Shingles, leaks, age, and structural issues',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100'
  },
  { 
    key: 'FOUNDATION', 
    title: 'Foundation', 
    icon: <Layers className="w-5 h-5" />,
    description: 'Cracks, settling, moisture, and stability',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100'
  },
  { 
    key: 'HVAC', 
    title: 'HVAC', 
    icon: <Wind className="w-5 h-5" />,
    description: 'Heating, cooling, ductwork, and efficiency',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100'
  },
  { 
    key: 'PLUMBING', 
    title: 'Plumbing', 
    icon: <Droplets className="w-5 h-5" />,
    description: 'Pipes, fixtures, water heater, and leaks',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100'
  },
  { 
    key: 'ELECTRICAL', 
    title: 'Electrical', 
    icon: <Zap className="w-5 h-5" />,
    description: 'Panel, wiring, outlets, and safety issues',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100'
  },
];

const Step2DocumentPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoContent, setVideoContent] = useState<VideoContent[]>([]);
  const [activeVideo, setActiveVideo] = useState<VideoContent | null>(null);
  const [completedVideos, setCompletedVideos] = useState<Set<string>>(new Set());
  const [maintenanceProgress, setMaintenanceProgress] = useState<Record<string, boolean>>({});
  
  // Collapsible state for sections
  const [isVideosExpanded, setIsVideosExpanded] = useState(true); // Videos start expanded
  const [isChecklistsExpanded, setIsChecklistsExpanded] = useState(false); // Checklists start collapsed
  
  // Save status for progress persistence
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | 'error' | null>(null);
  
  // Inline checklist state
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [property, setProperty] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  
  // Action video state - shown in activity section, not video section
  const [actionVideo, setActionVideo] = useState<VideoContent | null>(null);
  const [showActionVideo, setShowActionVideo] = useState(false);
  
  // Summary view state - shown when all checklists are complete
  const [showSummary, setShowSummary] = useState(false);

  // Load Montserrat font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Fetch module content and maintenance progress
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch Step 2 module content
        const moduleRes = await apiClient.courses.getModule('module-02', {});

        const videos: VideoContent[] = [];
        let actionVid: VideoContent | null = null;

        if (moduleRes.data.success && moduleRes.data.data.content) {
          moduleRes.data.data.content.forEach((content: any) => {
            const metadata = content.metadata || {};
            
            // Skip transition videos (not used on this page)
            if (metadata.isTransitionVideo) {
              return;
            }
            
            // Only include intro videos (the 7 contextual slides)
            if (metadata.sectionGroup === 'intro') {
              const videoItem: VideoContent = {
                id: content.id,
                title: metadata.title || 'Video',
                description: metadata.description || '',
                duration: formatDuration(metadata.duration || 0),
                videoUrl: content.contentUrl || '',
                isPlaceholder: metadata.isPlaceholder,
                isActionVideo: metadata.isActionVideo
              };
              
              // Separate action videos - they go to activity section
              if (metadata.isActionVideo) {
                actionVid = videoItem;
              } else {
                videos.push(videoItem);
              }
            }
          });
        }

        setVideoContent(videos);
        setActionVideo(actionVid);

        // Auto-select first video
        if (videos.length > 0) {
          setActiveVideo(videos[0]);
        }

        // Fetch maintenance progress and property/report data
        try {
          const profileRes = await apiClient.users.getProfile();
          const profile = profileRes.data.data.profile || profileRes.data.data;
          if (profile.properties?.length > 0) {
            const userProperty = profile.properties[0];
            setProperty(userProperty);
            
            // Get or create maintenance report
            let reportData;
            try {
              const reportRes = await apiClient.maintenance.getReportByProperty(userProperty.id);
              if (reportRes.data.success) {
                reportData = reportRes.data.data;
                // Build progress from existing findings
                if (reportData?.findings) {
                  const progress: Record<string, boolean> = {};
                  reportData.findings.forEach((f: any) => {
                    progress[f.category] = true;
                  });
                  setMaintenanceProgress(progress);
                }
              }
            } catch {
              // Create new report
              const createRes = await apiClient.maintenance.createReport(userProperty.id);
              if (createRes.data.success) {
                reportData = createRes.data.data;
              }
            }
            
            if (reportData) {
              setReport(reportData);
            }
          }
        } catch {
          // No maintenance data yet - that's fine
        }
      } catch (err: any) {
        console.error('Error fetching Step 2 content:', err);
        setError('Failed to load course content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchContent();
    }
  }, [user]);

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVideoComplete = () => {
    if (activeVideo) {
      const newCompleted = new Set(completedVideos);
      newCompleted.add(activeVideo.id);
      setCompletedVideos(newCompleted);

      // Auto-advance to next video
      const currentIndex = videoContent.findIndex(v => v.id === activeVideo.id);
      if (currentIndex < videoContent.length - 1) {
        setTimeout(() => {
          setActiveVideo(videoContent[currentIndex + 1]);
        }, 1500);
      }
    }
  };

  const calculateProgress = (): number => {
    const totalCategories = CATEGORIES.length;
    const completedCategories = Object.keys(maintenanceProgress).length;
    return Math.round((completedCategories / totalCategories) * 100);
  };

  const getCompletedCategoryCount = (): number => {
    return Object.keys(maintenanceProgress).length;
  };

  // Handle category selection - show inline checklist instead of navigating
  // Creates an empty finding if none exists, so photos can be uploaded immediately
  const handleCategorySelect = async (category: Category) => {
    setActiveCategory(category);
    setActiveVideo(null); // Clear video when showing checklist
    setShowActionVideo(false); // Clear action video when showing checklist
    setIsVideosExpanded(false); // Auto-collapse video section for cleaner UX
    setIsChecklistsExpanded(true); // Make sure checklists section is visible
    
    // Scroll to top of page for better UX, especially for items further down
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Eagerly create an empty finding if one doesn't exist
    // This enables photo uploads on the first pass through the checklist
    const existingFinding = report?.findings?.find((f: any) => f.category === category);
    if (!existingFinding && property && report) {
      try {
        // Create an empty finding for this category
        await apiClient.maintenance.saveFinding({
          reportId: report.id,
          category: category,
          checklistData: {}, // Empty checklist data - will be populated when user saves
        });
        
        // Refresh report to get the new finding with its ID
        const refreshRes = await apiClient.maintenance.getReportByProperty(property.id);
        if (refreshRes.data.success) {
          setReport(refreshRes.data.data);
        }
      } catch (error) {
        console.error('Error creating initial finding for photo uploads:', error);
        // Non-critical - user can still use checklist, just won't have immediate photo uploads
      }
    }
  };

  // Get the next category in the sequence
  const getNextCategory = (currentCategory: Category): Category | null => {
    const currentIndex = CATEGORIES.findIndex(cat => cat.key === currentCategory);
    if (currentIndex < CATEGORIES.length - 1) {
      return CATEGORIES[currentIndex + 1].key;
    }
    return null; // No more categories
  };

  // Handle saving a finding from a checklist
  const handleSaveFinding = async (data: any) => {
    let currentReport = report;
    
    // If report doesn't exist, try to create one
    if (!currentReport) {
      if (!property) {
        console.error('Cannot save finding: No property available');
        toast.error('Unable to save — no property found on your account. Please refresh the page and try again.');
        return;
      }
      
      try {
        // Try to get existing report first
        const reportRes = await apiClient.maintenance.getReportByProperty(property.id);
        if (reportRes.data.success) {
          currentReport = reportRes.data.data;
          setReport(currentReport);
        }
      } catch {
        // Create new report if none exists
        try {
          const createRes = await apiClient.maintenance.createReport(property.id);
          if (createRes.data.success) {
            currentReport = createRes.data.data;
            setReport(currentReport);
          }
        } catch (createErr) {
          console.error('Failed to create maintenance report:', createErr);
          toast.error('Could not create a maintenance report for your property. Please check your connection and try again.');
          return;
        }
      }
      
      if (!currentReport) {
        console.error('Failed to get or create maintenance report');
        toast.error('Unable to initialize your maintenance report. Please refresh the page and try again.');
        return;
      }
    }
    
    try {
      const res = await apiClient.maintenance.saveFinding({
        reportId: currentReport.id,
        ...data
      });
      
      if (res.data.success) {
        // Update local report with new finding (create new array to ensure React detects change)
        setReport((prev: any) => {
          const prevFindings = prev?.findings || [];
          const existingIndex = prevFindings.findIndex((f: any) => f.category === data.category);
          
          // Create a new array to avoid mutation issues
          const newFindings = [...prevFindings];
          if (existingIndex >= 0) {
            newFindings[existingIndex] = res.data.data;
          } else {
            newFindings.push(res.data.data);
          }
          
          return { ...(prev || currentReport), findings: newFindings };
        });
        
        // Update maintenance progress
        const newProgress = {
          ...maintenanceProgress,
          [data.category]: true
        };
        setMaintenanceProgress(newProgress);
        
        // Calculate and save module progress
        const completedCount = Object.keys(newProgress).length;
        const progressPercentage = Math.round((completedCount / CATEGORIES.length) * 100);
        
        console.log(`📊 Saving Step 2 progress: ${progressPercentage}% (${completedCount}/${CATEGORIES.length} checklists)`);
        setSaveStatus('saving');
        
        try {
          await apiClient.courses.updateProgress(STEP2_MODULE_ID, {
            progressPercentage,
            timeSpent: 0
          });
          
          // Mark module complete when all 5 checklists are done
          if (completedCount >= CATEGORIES.length) {
            console.log('✅ All Step 2 checklists complete - marking module as complete');
            await apiClient.courses.completeModule(STEP2_MODULE_ID);
          }
          
          console.log(`✅ Step 2 progress saved: ${progressPercentage}%`);
          setSaveStatus('saved');
          toast.success('Assessment saved!');
          setTimeout(() => setSaveStatus(null), 2000);
        } catch (progressErr) {
          console.error('Failed to save Step 2 progress:', progressErr);
          setSaveStatus('error');
          setTimeout(() => setSaveStatus(null), 3000);
        }
        
        // Auto-navigate to next checklist, or show summary if done
        const nextCategory = getNextCategory(data.category as Category);
        if (nextCategory) {
          // Navigate to the next checklist
          setActiveCategory(nextCategory);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          // All checklists complete - show summary view
          setActiveCategory(null);
          setActiveVideo(null);
          setShowActionVideo(false);
          setShowSummary(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error: any) {
      console.error('Error saving finding:', error);
      // Extract backend validation message if available
      const backendMessage = error?.response?.data?.error?.message;
      if (backendMessage) {
        toast.error(`Could not save: ${backendMessage}`);
      } else {
        toast.error('Something went wrong while saving your assessment. Please check your connection and try again.');
      }
    }
  };

  // Get finding data for a category
  const getFindingForCategory = (category: Category) => {
    return report?.findings?.find((f: any) => f.category === category);
  };

  // Handle evidence upload
  const handleEvidenceUpload = () => {
    // Refresh report to include new evidence
    if (property) {
      apiClient.maintenance.getReportByProperty(property.id)
        .then(res => {
          if (res.data.success) {
            setReport(res.data.data);
          }
        });
    }
  };

  // Go back to video from checklist
  const handleBackFromChecklist = () => {
    setActiveCategory(null);
    // Go back to action video (in learning section) if available, otherwise first regular video
    if (actionVideo) {
      setShowActionVideo(true);
      setActiveVideo(null);
      setIsVideosExpanded(true); // Expand learning section since action video is there now
      setIsChecklistsExpanded(false);
    } else if (videoContent.length > 0) {
      setActiveVideo(videoContent[0]);
      setIsVideosExpanded(true);
      setIsChecklistsExpanded(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading Step 2 content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Content</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Redirect to dashboard if no property - user needs to complete property onboarding first
  if (!property) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Property Required</h2>
          <p className="text-slate-600 mb-4">Please add your property details to continue with the course.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>

            {/* Progress */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-teal-600">{getCompletedCategoryCount()}/{CATEGORIES.length}</span> checklists complete
              </div>
              {/* Save Status Indicator */}
              {saveStatus && (
                <div className={`text-xs font-medium px-2 py-1 rounded transition-all ${
                  saveStatus === 'saving' ? 'text-slate-500 bg-slate-100' :
                  saveStatus === 'saved' ? 'text-emerald-600 bg-emerald-50' :
                  'text-red-600 bg-red-50'
                }`}>
                  {saveStatus === 'saving' ? 'Saving...' :
                   saveStatus === 'saved' ? '✓ Saved' :
                   '✗ Save failed'}
                </div>
              )}
              <div className="w-32 h-2 bg-slate-200 rounded-full">
                <div 
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Main Content - Left Navigation, Right Video */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* LEFT SIDE - Navigation & Checklists */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden sticky top-24">
              {/* Step Header - Primary Identity */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="pl-6">
                  <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-0.5">Step 1</p>
                  <h2 className="text-base font-bold text-slate-800 leading-tight">Document Your Home's Issues</h2>
                </div>
              </div>

              {/* Video Navigation Section - Collapsible */}
              <div className="border-b border-slate-100">
                {/* Collapsible Header */}
                <button
                  onClick={() => {
                    const willExpand = !isVideosExpanded;
                    setIsVideosExpanded(willExpand);
                    // Auto-collapse checklists when expanding videos
                    if (willExpand) {
                      setIsChecklistsExpanded(false);
                    }
                  }}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">Learn About Deferred Maintenance</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({completedVideos.size}/{videoContent.length + (actionVideo ? 1 : 0)})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isVideosExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isVideosExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-4 pb-4 space-y-2">
                    {videoContent.map((video, index) => {
                      const isActive = video.id === activeVideo?.id && !showActionVideo;
                      const isCompleted = completedVideos.has(video.id);

                      return (
                        <button
                          key={video.id}
                          onClick={() => {
                            setActiveVideo(video);
                            setActiveCategory(null); // Clear checklist when selecting video
                            setShowActionVideo(false); // Clear action video when selecting regular video
                            setShowSummary(false); // Clear summary view
                            window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
                          }}
                          className={`w-full text-left p-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-teal-50 border-2 border-teal-300'
                              : isCompleted
                              ? 'bg-emerald-50 border-2 border-emerald-200'
                              : 'bg-slate-50 border-2 border-slate-100 hover:border-slate-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isCompleted ? 'bg-emerald-200' : isActive ? 'bg-teal-200' : 'bg-slate-200'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                              ) : isActive ? (
                                <Play className="w-4 h-4 text-teal-700" />
                              ) : (
                                <span className="text-slate-600 font-bold text-sm">{index + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-medium text-sm leading-tight ${
                                isActive ? 'text-teal-900' : isCompleted ? 'text-emerald-800' : 'text-slate-900'
                              }`}>
                                {video.title}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                <Clock className="w-3 h-3" />
                                <span>{video.duration}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    
                    {/* Action Video - Walkthrough Guide (Transition to Activity) */}
                    {actionVideo && (
                      <button
                        onClick={() => {
                          setShowActionVideo(true);
                          setActiveCategory(null);
                          setActiveVideo(null);
                          setShowSummary(false);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-full text-left p-3 rounded-xl transition-all ${
                          showActionVideo
                            ? 'bg-teal-50 border-2 border-teal-300'
                            : completedVideos.has(actionVideo.id)
                            ? 'bg-emerald-50 border-2 border-emerald-200'
                            : 'bg-slate-50 border-2 border-slate-100 hover:border-slate-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            completedVideos.has(actionVideo.id) ? 'bg-emerald-200' : showActionVideo ? 'bg-teal-200' : 'bg-slate-200'
                          }`}>
                            {completedVideos.has(actionVideo.id) ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                            ) : showActionVideo ? (
                              <Play className="w-4 h-4 text-teal-700" />
                            ) : (
                              <span className="text-slate-600 font-bold text-sm">{videoContent.length + 1}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-medium text-sm leading-tight ${
                              showActionVideo ? 'text-teal-900' : completedVideos.has(actionVideo.id) ? 'text-emerald-800' : 'text-slate-900'
                            }`}>
                              {actionVideo.title}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                              <Clock className="w-3 h-3" />
                              <span>{actionVideo.duration}</span>
                            </div>
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Checklists Section - Collapsible */}
              <div className="bg-gradient-to-b from-teal-50 to-white">
                {/* Collapsible Header */}
                <button
                  onClick={() => {
                    const willExpand = !isChecklistsExpanded;
                    setIsChecklistsExpanded(willExpand);
                    // Auto-collapse videos when expanding checklists
                    if (willExpand) {
                      setIsVideosExpanded(false);
                    }
                  }}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-teal-100/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">Document Your Home</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({getCompletedCategoryCount()}/{CATEGORIES.length})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isChecklistsExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isChecklistsExpanded ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-slate-600 mb-4">
                      Work through each checklist to document issues in your home.
                    </p>
                    
                    <div className="space-y-2">
                      {CATEGORIES.map((cat) => {
                        const isCompleted = maintenanceProgress[cat.key];
                        const isActive = activeCategory === cat.key;

                        return (
                          <button
                            key={cat.key}
                            onClick={() => handleCategorySelect(cat.key)}
                            className={`w-full text-left p-3 rounded-xl transition-all hover:shadow-md ${
                              isActive
                                ? 'bg-teal-200 border-2 border-teal-400 shadow-md'
                                : isCompleted
                                ? 'bg-emerald-100 border-2 border-emerald-200'
                                : 'bg-white border-2 border-slate-100 hover:border-teal-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 ${cat.bgColor} rounded-lg flex items-center justify-center ${cat.color}`}>
                                  {cat.icon}
                                </div>
                                <div>
                                  <h4 className="font-medium text-slate-900">{cat.title}</h4>
                                  <p className="text-xs text-slate-500">{cat.description}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isCompleted && (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                )}
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* View Summary Button - Enabled when all checklists complete */}
                    {getCompletedCategoryCount() >= CATEGORIES.length ? (
                      <button
                        onClick={() => {
                          setShowSummary(true);
                          setActiveCategory(null);
                          setActiveVideo(null);
                          setShowActionVideo(false);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-full mt-4 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                          showSummary
                            ? 'bg-emerald-600 text-white ring-2 ring-emerald-300'
                            : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-lg hover:shadow-xl'
                        }`}
                      >
                        <CheckCircle2 className="w-5 h-5" />
                        View Summary & Complete
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full mt-4 py-3 px-4 bg-slate-100 text-slate-400 rounded-xl font-medium cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <Clock className="w-5 h-5" />
                        Complete All Checklists to View Summary
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Video Player OR Inline Checklist OR Action Video OR Summary */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            {/* Show Summary when all checklists complete and summary view is active */}
            {showSummary ? (
              <ChecklistSummary
                findings={report?.findings || []}
                onBack={() => {
                  setShowSummary(false);
                  // Go back to last checklist for editing
                  setActiveCategory('ELECTRICAL');
                  setIsChecklistsExpanded(true);
                  setIsVideosExpanded(false);
                }}
                onNavigateToStep3={() => {
                  // Navigate to dashboard - it will guide user to next appropriate step based on timeline
                  navigate('/dashboard');
                }}
                hasNoticeReceived={!!property?.lastAppraisalNoticeDate}
              />
            ) : showActionVideo && actionVideo ? (
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                <VideoPlayer
                  key={actionVideo.id}
                  videoUrl={actionVideo.videoUrl}
                  onVideoComplete={() => {
                    // When action video completes, mark it and show checklists prompt
                    setCompletedVideos(prev => new Set([...prev, actionVideo.id]));
                  }}
                  currentProgress={0}
                  onProgress={() => {}}
                />
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-slate-900">
                          {actionVideo.title}
                        </h3>
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-bold uppercase">
                          Walkthrough
                        </span>
                      </div>
                      <p className="text-slate-600">{actionVideo.description}</p>
                    </div>
                    {completedVideos.has(actionVideo.id) && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                        <CheckCircle2 className="w-4 h-4" />
                        Watched
                      </div>
                    )}
                  </div>
                  
                  {/* Transition CTA - Your Next Step */}
                  <div className="mt-6 p-5 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border-2 border-teal-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <ClipboardCheck className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-slate-900 mb-1">Your Next Step: Document Your Home</h4>
                        <p className="text-slate-600 text-sm mb-4">
                          Now that you understand what to look for, walk through each checklist to document issues in your home. Start with your roof and work your way through all 5 systems.
                        </p>
                        <button
                          onClick={() => {
                            setShowActionVideo(false);
                            setIsVideosExpanded(false);
                            setIsChecklistsExpanded(true);
                            handleCategorySelect('ROOF'); // Start with Roof checklist
                          }}
                          className="px-5 py-2.5 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                        >
                          <ClipboardCheck className="w-4 h-4" />
                          Start with Roof Checklist
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeCategory ? (
              /* Show Checklist when a category is active */
              <div className="space-y-6">
                {activeCategory === 'ROOF' && (
                  <RoofChecklist
                    initialData={getFindingForCategory('ROOF')}
                    onSave={handleSaveFinding}
                    onBack={handleBackFromChecklist}
                    findingId={getFindingForCategory('ROOF')?.id}
                    evidence={getFindingForCategory('ROOF')?.evidence || []}
                    invoices={getFindingForCategory('ROOF')?.invoices || []}
                    onEvidenceChange={handleEvidenceUpload}
                  />
                )}
                {activeCategory === 'FOUNDATION' && (
                  <FoundationChecklist
                    initialData={getFindingForCategory('FOUNDATION')}
                    onSave={handleSaveFinding}
                    onBack={handleBackFromChecklist}
                    findingId={getFindingForCategory('FOUNDATION')?.id}
                    evidence={getFindingForCategory('FOUNDATION')?.evidence || []}
                    invoices={getFindingForCategory('FOUNDATION')?.invoices || []}
                    onEvidenceChange={handleEvidenceUpload}
                  />
                )}
                {activeCategory === 'HVAC' && (
                  <HVACChecklist
                    initialData={getFindingForCategory('HVAC')}
                    onSave={handleSaveFinding}
                    onBack={handleBackFromChecklist}
                    findingId={getFindingForCategory('HVAC')?.id}
                    evidence={getFindingForCategory('HVAC')?.evidence || []}
                    invoices={getFindingForCategory('HVAC')?.invoices || []}
                    onEvidenceChange={handleEvidenceUpload}
                  />
                )}
                {activeCategory === 'PLUMBING' && (
                  <PlumbingChecklist
                    initialData={getFindingForCategory('PLUMBING')}
                    onSave={handleSaveFinding}
                    onBack={handleBackFromChecklist}
                    findingId={getFindingForCategory('PLUMBING')?.id}
                    evidence={getFindingForCategory('PLUMBING')?.evidence || []}
                    invoices={getFindingForCategory('PLUMBING')?.invoices || []}
                    onEvidenceChange={handleEvidenceUpload}
                  />
                )}
                {activeCategory === 'ELECTRICAL' && (
                  <ElectricalChecklist
                    initialData={getFindingForCategory('ELECTRICAL')}
                    onSave={handleSaveFinding}
                    onBack={handleBackFromChecklist}
                    findingId={getFindingForCategory('ELECTRICAL')?.id}
                    evidence={getFindingForCategory('ELECTRICAL')?.evidence || []}
                    invoices={getFindingForCategory('ELECTRICAL')?.invoices || []}
                    onEvidenceChange={handleEvidenceUpload}
                  />
                )}
              </div>
            ) : (
              /* Show Video Player when no category is active */
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                {activeVideo ? (
                  <>
                    <VideoPlayer
                      key={activeVideo.id}
                      videoUrl={activeVideo.videoUrl}
                      onVideoComplete={handleVideoComplete}
                      currentProgress={0}
                      onProgress={() => {}}
                    />
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 mb-2">
                            {activeVideo.title}
                          </h3>
                          <p className="text-slate-600">{activeVideo.description}</p>
                        </div>
                        {completedVideos.has(activeVideo.id) && (
                          <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                            <CheckCircle2 className="w-4 h-4" />
                            Watched
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="aspect-video bg-slate-100 flex items-center justify-center">
                    <p className="text-slate-500">Select a video to start learning</p>
                  </div>
                )}
              </div>
            )}

            {/* Bottom Navigation — hidden when summary is visible (summary has its own CTAs) */}
            {!showSummary && (
              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Dashboard
                </button>

                {/* Right CTA: Next Video when viewing videos and more available, otherwise Continue to Step 2 when ready */}
                {(() => {
                  // When viewing a video, check if there's a next one
                  if (!activeCategory && activeVideo) {
                    const currentIndex = videoContent.findIndex(v => v.id === activeVideo.id);
                    const hasNextVideo = currentIndex < videoContent.length - 1;
                    
                    if (hasNextVideo) {
                      return (
                        <button
                          onClick={() => {
                            setActiveVideo(videoContent[currentIndex + 1]);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                        >
                          Next Video
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      );
                    }
                  }
                  
                  // Show Continue to Step 2 when progress is sufficient (and not showing Next Video)
                  if (calculateProgress() >= 60) {
                    return (
                      <button
                        onClick={() => navigate('/step/2/file-appeal')}
                        className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                      >
                        Continue to Step 2
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    );
                  }
                  
                  return null;
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2DocumentPage;
